import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useIsAutoPopulationDone } from './useIsAutoPopulationDone';
import { useIsAccommodationsAiRecommendationsEnabled } from '../../useIsAccommodationsAiRecommendationsEnabled';
import { hasEnoughTimeElapsedSinceDate } from '@shared/utils/dateHelpers';
import { useEventInfo } from '@shared/utils/eventInfo';

interface UseAutoPopulationFeatureFlagsReturn
  extends Readonly<{
    asyncHotelAutopopulationEnabled: boolean;
    isAccommodationsAiRecommendationsEnabled: boolean;
    hasEnoughTimeElapsed: boolean;
    isAutoPopulationDoneFromStorage: boolean;
    setIsAutoPopulationDone: () => void;
    isLoadingFeatureFlags: boolean;
  }> {}

/**
 * Extracts and consolidates all feature flag logic for auto-population.
 */
export const useAutoPopulationFeatureFlags = (validateIsEnoughTimeElapsed?: boolean): UseAutoPopulationFeatureFlagsReturn => {
  const [, loadingDialog, , dialogPayload] = useIsFeatureEnabled('showAutoPopulationCompletedDialog');
  const { eventInfo, loading: eventInfoLoading } = useEventInfo();

  const durationHours = (dialogPayload as { durationHours?: number })?.durationHours ?? 0;
  const hasEnoughTimeElapsed = hasEnoughTimeElapsedSinceDate(durationHours, new Date(eventInfo?.firebaseCreateAt?.timestamp));

  // Feature flag to control whether the async hotel autopopulation is enabled
  const [asyncHotelAutopopulationEnabled, loadingAsyncHotelAutopulationEnabled] = useIsFeatureEnabled('asyncHotelAutopopulationEnabled');

  // Feature flag to control whether the accommodations AI recommendations are enabled
  const { isAccommodationsAiRecommendationsEnabled, isLoading: loadingIsAccommodationsAiRecommendationsEnabled } = useIsAccommodationsAiRecommendationsEnabled();

  const { isAutoPopulationDone: isAutoPopulationDoneFromStorage, setIsAutoPopulationDone, isLoadingAutoPopulationFlag } = useIsAutoPopulationDone();

  const isLoadingFeatureFlags =
    loadingDialog || eventInfoLoading || loadingAsyncHotelAutopulationEnabled || loadingIsAccommodationsAiRecommendationsEnabled || isLoadingAutoPopulationFlag;
  return {
    asyncHotelAutopopulationEnabled,
    isAccommodationsAiRecommendationsEnabled,
    hasEnoughTimeElapsed: validateIsEnoughTimeElapsed ? hasEnoughTimeElapsed : true,
    isAutoPopulationDoneFromStorage,
    setIsAutoPopulationDone,
    isLoadingFeatureFlags,
  };
};
